import KnxProductData from "../KnxProductdata";
import Logger from "../Logger";
import { EP } from "../performance/PerfomanceItem";
import { vbscriptStandartFuncs } from "../transpiler/transpileVbScriptToJavaScript";
import { isUiModeDeveloper } from "../uiMode";
import KnxInstanceData, { ValueLevels } from "./KnxInstanceData";
import JSON5 from "json5";
import { isFloatOrNumberString } from "./utils";

const logger = new Logger();

export function evalInContext(name: string, code: string, inputVars: any, outVars: Array<string>): any {
  let sInputVars = "";
  for (let name in inputVars) {
    sInputVars += `let ${name} = ${inputVars[name]} \n`;
  }

  let sOutputVars = "{";

  let i = 0;
  let l = outVars.length;
  outVars.forEach((name) => {
    sOutputVars += `${name}: ${name}`;
    i++;
    if (i < l) {
      sOutputVars += ", ";
    }
  });

  sOutputVars += "}";

  const functionString = `
    ${sInputVars}

    ${code}

    return ${sOutputVars}
`;

  let res: any = {};
  try {
    const myFunction = new Function(functionString);
    res = myFunction();
  } catch (ex: any) {
    const sError = `Error evaluating ParameterCalculation (${name}): ${ex.message} \n ${code}`;

    logger.error(sError);
    if (isUiModeDeveloper()) {
      alert(sError);
    }
  }
  return res;
}

export class KnxParameterCalculationReference {
  side: "L" | "R";
  knxParameterCalculation: KnxParameterCalculation;

  constructor(side: "L" | "R" = "L", knxParameterCalculation: KnxParameterCalculation) {
    this.side = side;
    this.knxParameterCalculation = knxParameterCalculation;
  }
}

export default class KnxParameterCalculation {
  language = "";

  name = "";
  moduleId = "";
  id = "";

  // parameter mapping

  lParams = new Map<string, string>();
  rParams = new Map<string, string>();

  // code

  rlTransformation: string | undefined;
  lrTransformation: string | undefined;

  lrTransformationFuncName?: string; // name of the global script function
  rlTransformationFuncName?: string; // name of the global script function

  rlTransformationParams?: string; // paramter for the global script context of  "function Validate_HCL_LR(input, output, context)"
  lrTransformationParams?: string;

  // execution

  eval(
    rightToLeft: boolean,
    knxProductData: KnxProductData,
    knxInstanceData: KnxInstanceData,
    instanceId: string,
    id: string,
  ) {
    EP(
      "KnxParameterCalculation-" + id, // no itx
    ).run(() => {
      ///////////////////////////////////////////////////////////
      const vars: any = {};
      const self = this;
      logger.log(`Calculation: ${this.id}, rightToLeft: ${rightToLeft}`);
      //
      // evaluate code in an isolated context with local vars
      //

      //
      // load input params AND output params to local evaluation context vars
      //
      [...this.lParams, ...this.rParams].forEach((entry) => {
        const key = entry[0];
        const value = entry[1];

        // because jsvascript handles expressions like "3" + 1 = "31"
        // in an unintended way we have to manage types for vaiables

        //  !! HACK !! HACK!! HACK!! HACK
        // for now we expect they are all numbers
        let v1 = knxInstanceData.getValue(key, instanceId);
        let v2 = knxInstanceData.getValue(key, instanceId, true);
        if (v1 != v2) {
          logger.log(`Inputparam ${key} unreferenced mismatch value: ${v2} for calculation ${this.id}`);
        }
        v1 = v2;

        if (v1 == undefined) {
          logger.log(`Parameter undefined: ${key}, calculation: ${this.id}`);
        }

        let v: any;
        if (isFloatOrNumberString(v1)) {
          v = parseFloat(v1);
        } else {
          v = `"${v1}"`;
        }

        vars[value] = v;

        // logger.log(`calcInput: ${value} <= ${key}=${v}`);
      });

      const outputVarnames = new Array<string>();
      (rightToLeft ? this.lParams : this.rParams).forEach((value, key) => {
        outputVarnames.push(value);
      });

      for (let name in vbscriptStandartFuncs) {
        vars[name] = vbscriptStandartFuncs[name];
      }

      let res = {};

      if (this.rlTransformationFuncName || this.lrTransformationFuncName) {
        const input = { ...vars };

        const output = {};

        if (knxProductData.knxScriptEngine) {
          if (rightToLeft && this.rlTransformationFuncName) {
            console.log("MWE1 call : ", this.rlTransformationFuncName, vars);
            const context = this.rlTransformationParams ? JSON5.parse(this.rlTransformationParams) : {};
            const params = [input, output, context];
            const xres = knxProductData.knxScriptEngine.call(this.rlTransformationFuncName, params, {});
            res = { ...output };
          } else if (!rightToLeft && this.lrTransformationFuncName) {
            console.log("MWE2 call : ", this.lrTransformationFuncName, vars);
            const context = this.lrTransformationParams ? JSON5.parse(this.lrTransformationParams) : {};
            const params = [input, output, context];
            const xres = knxProductData.knxScriptEngine.call(this.lrTransformationFuncName, params, {});
            res = { ...output };
          } else {
            logger.error(`Function name not found for calculation: ${this.id}`);
          }
        } else {
          logger.error(`ScriptEngine not found for calculation: ${this.id}`);
        }

        console.log("MWE1 call done: ", res);
      } else {
        let code = "";

        if (rightToLeft) {
          if (self.rlTransformation) {
            code = self.rlTransformation;
          }
        } else {
          if (self.lrTransformation) {
            code = self.lrTransformation;
          }
        }

        res = evalInContext(this.name, code, vars, outputVarnames);
      }

      //
      // set output params from local evaluation context vars
      //

      const resultParams = rightToLeft ? this.lParams : this.rParams;
      const resultLevel = rightToLeft ? ValueLevels.calculatedRightToLeft : ValueLevels.calculatedLeftToRight;

      resultParams.forEach((varName, paramId) => {
        //
        // store all results (AS STRINGS!! )
        //

        const v1 = res[varName];
        if (v1 == undefined) {
          logger.error(`Output var ${varName} => ${paramId} is undefined`);
        } else {
          const v = `${res[varName]}`;
          knxInstanceData.setValue(resultLevel, v, paramId, instanceId, this.moduleId, true, true);

          // if (isNaN(res[varName]) || res[varName] == undefined) {
          //   logger.log(`Output var ${varName} => ${paramId} is undefined or NaN ${v}`);
          // } else {
          //   knxInstanceData.setValue(resultLevel, v, paramId, instanceId, this.moduleId, true, true);
          //   // knxInstanceData.setReferenced(key, instanceId); // no itx
          // }

          logger.log(`calcRes: ${paramId} <= ${varName}=${v}`);
        }
      });
    });
  }
}
